import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import RegistrationHeader from './components/RegistrationHeader';
import ProgressIndicator from './components/ProgressIndicator';
import CompanyInfoStep from './components/CompanyInfoStep';
import ServicesStep from './components/ServicesStep';
import ServiceAreaStep from './components/ServiceAreaStep';
import DocumentsStep from './components/DocumentsStep';
import NavigationButtons from './components/NavigationButtons';
import HelpSidebar from './components/HelpSidebar';

const LeverancierRegistration = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Company Info
    companyName: '',
    kvkNumber: '',
    companyType: '',
    vatNumber: '',
    contactPerson: '',
    jobTitle: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    website: '',
    foundedYear: '',
    address: '',
    postalCode: '',
    city: '',
    province: '',
    
    // Step 2 - Services
    selectedServices: [],
    solarCapacity: '',
    solarBrands: '',
    heatpumpTypes: '',
    heatpumpBrands: '',
    batteryCapacity: '',
    batteryBrands: '',
    companyDescription: '',
    employeeCount: '',
    
    // Step 3 - Service Area
    serviceRadius: '',
    serviceProvinces: [],
    servicePostalCodes: [],
    
    // Step 4 - Documents
    documents: {}
  });
  const [errors, setErrors] = useState({});

  const totalSteps = 4;

  // Load saved draft from localStorage on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('leverancierRegistrationDraft');
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setFormData(parsedDraft);
      } catch (error) {
        console.error('Error loading saved draft:', error);
      }
    }
  }, []);

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear related errors when data is updated
    const updatedFields = Object.keys(updates);
    setErrors(prev => {
      const newErrors = { ...prev };
      updatedFields?.forEach(field => {
        delete newErrors?.[field];
      });
      return newErrors;
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.companyName?.trim()) newErrors.companyName = 'Bedrijfsnaam is verplicht';
        if (!formData?.kvkNumber?.trim()) newErrors.kvkNumber = 'KvK nummer is verplicht';
        if (!formData?.companyType) newErrors.companyType = 'Bedrijfsvorm is verplicht';
        if (!formData?.contactPerson?.trim()) newErrors.contactPerson = 'Contactpersoon is verplicht';
        if (!formData?.email?.trim()) newErrors.email = 'E-mailadres is verplicht';
        if (!formData?.password?.trim()) newErrors.password = 'Wachtwoord is verplicht';
        if (formData?.password && formData?.password?.length < 8) {
          newErrors.password = 'Wachtwoord moet minimaal 8 karakters bevatten';
        }
        if (formData?.password !== formData?.confirmPassword) {
          newErrors.confirmPassword = 'Wachtwoorden komen niet overeen';
        }
        if (!formData?.phone?.trim()) newErrors.phone = 'Telefoonnummer is verplicht';
        if (!formData?.address?.trim()) newErrors.address = 'Adres is verplicht';
        if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postcode is verplicht';
        if (!formData?.city?.trim()) newErrors.city = 'Plaats is verplicht';
        if (!formData?.province?.trim()) newErrors.province = 'Provincie is verplicht';
        
        // Email validation
        if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
          newErrors.email = 'Ongeldig e-mailadres';
        }
        
        // KvK number validation (8 digits)
        if (formData?.kvkNumber && !/^\d{8}$/?.test(formData?.kvkNumber?.replace(/\s/g, ''))) {
          newErrors.kvkNumber = 'KvK nummer moet 8 cijfers bevatten';
        }
        break;

      case 2:
        if (!formData?.selectedServices || formData?.selectedServices?.length === 0) {
          newErrors.selectedServices = 'Selecteer minimaal één dienst categorie';
        }
        break;

      case 3:
        if (!formData?.serviceRadius || formData?.serviceRadius < 1) {
          newErrors.serviceRadius = 'Service straal is verplicht';
        }
        if (!formData?.serviceProvinces || formData?.serviceProvinces?.length === 0) {
          newErrors.serviceProvinces = 'Selecteer minimaal één provincie';
        }
        break;

      case 4:
        // Check required documents
        const requiredDocs = ['kvk', 'insurance'];
        requiredDocs?.forEach(docType => {
          if (!formData?.documents?.[docType]) {
            newErrors[docType] = 'Dit document is verplicht';
          }
        });
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveDraft = () => {
    try {
      localStorage.setItem('leverancierRegistrationDraft', JSON.stringify(formData));
      alert('Concept opgeslagen! U kunt later verder gaan waar u gebleven bent.');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Er is een fout opgetreden bij het opslaan van het concept.');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        {
          fullName: formData?.contactPerson,
          userType: 'provider',
          phone: formData?.phone,
          postalCode: formData?.postalCode,
          companyName: formData?.companyName,
          kvkNumber: formData?.kvkNumber
        }
      );

      if (error) {
        console.error('Leverancier registratie fout:', error);
        const errorMessage = error?.message || 'Registratie mislukt. Probeer het opnieuw.';
        setErrors({ submit: errorMessage });
        alert(`Registratie fout: ${errorMessage}`);
      } else {
        console.log('Leverancier registratie succesvol:', data);
        // Clear saved draft
        localStorage.removeItem('leverancierRegistrationDraft');
        
        alert('Registratie succesvol! Controleer uw e-mail voor verificatie.');
        navigate('/leverancier-login');
      }
    } catch (error) {
      console.error('Onverwachte registratie fout:', error);
      const errorMessage = 'Er is een onverwachte fout opgetreden bij het verzenden van uw registratie. Probeer het opnieuw.';
      setErrors({ submit: errorMessage });
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInfoStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <ServicesStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <ServiceAreaStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 4:
        return (
          <DocumentsStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    // Basic check to enable/disable next button
    switch (currentStep) {
      case 1:
        return formData?.companyName && formData?.kvkNumber && formData?.email && formData?.password;
      case 2:
        return formData?.selectedServices && formData?.selectedServices?.length > 0;
      case 3:
        return formData?.serviceRadius && formData?.serviceProvinces && formData?.serviceProvinces?.length > 0;
      case 4:
        return formData?.documents?.kvk && formData?.documents?.insurance;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNavigation />

      <RegistrationHeader />
      
      <main className="pt-20 sm:pt-24 pb-24 sm:pb-32 lg:ml-[72px] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="max-w-4xl mx-auto">
            <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                <div className="bg-card border border-border rounded-lg p-6 lg:p-8">
                  {renderCurrentStep()}
                </div>
              </div>
              
              {/* Help Sidebar */}
              <div className="lg:col-span-1">
                <HelpSidebar currentStep={currentStep} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <NavigationButtons
        currentStep={currentStep}
        totalSteps={totalSteps}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        canProceed={canProceed()}
      />
    </div>
  );
};

export default LeverancierRegistration;